/**
 * Validación de NIF/NIE/CIF español con dígito de control.
 * - DNI: 8 dígitos + letra
 * - NIE: X/Y/Z + 7 dígitos + letra
 * - CIF: letra + 7 dígitos + dígito/letra de control
 *
 * Devuelve true si el formato y el dígito de control son válidos.
 */

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_CONTROL_LETTERS = "JABCDEFGHI";

const normalize = (raw: string): string =>
  (raw || "").toUpperCase().replace(/[^0-9A-Z]/g, "");

const validateDni = (id: string): boolean => {
  if (!/^\d{8}[A-Z]$/.test(id)) return false;
  const number = parseInt(id.slice(0, 8), 10);
  return DNI_LETTERS[number % 23] === id[8];
};

const validateNie = (id: string): boolean => {
  if (!/^[XYZ]\d{7}[A-Z]$/.test(id)) return false;
  const prefixMap: Record<string, string> = { X: "0", Y: "1", Z: "2" };
  const numericString = prefixMap[id[0]] + id.slice(1, 8);
  const number = parseInt(numericString, 10);
  return DNI_LETTERS[number % 23] === id[8];
};

const validateCif = (id: string): boolean => {
  if (!/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/.test(id)) return false;
  const digits = id.slice(1, 8);
  const control = id[8];

  let evenSum = 0;
  let oddSum = 0;
  for (let i = 0; i < 7; i++) {
    const n = parseInt(digits[i], 10);
    if ((i + 1) % 2 === 0) {
      evenSum += n;
    } else {
      const doubled = n * 2;
      oddSum += Math.floor(doubled / 10) + (doubled % 10);
    }
  }
  const total = evenSum + oddSum;
  const controlDigit = (10 - (total % 10)) % 10;

  // Tipo de organización determina si el control es número o letra
  const firstLetter = id[0];
  const onlyLetter = "PQRSNW".includes(firstLetter);
  const onlyNumber = "ABEH".includes(firstLetter);

  if (onlyNumber) return control === String(controlDigit);
  if (onlyLetter) return control === CIF_CONTROL_LETTERS[controlDigit];
  // Resto: vale número o letra
  return control === String(controlDigit) || control === CIF_CONTROL_LETTERS[controlDigit];
};

export const isValidSpanishTaxId = (raw: string | null | undefined): boolean => {
  const id = normalize(raw || "");
  if (!id) return false;
  if (/^\d{8}[A-Z]$/.test(id)) return validateDni(id);
  if (/^[XYZ]\d{7}[A-Z]$/.test(id)) return validateNie(id);
  if (/^[A-Z]\d{7}[0-9A-J]$/.test(id)) return validateCif(id);
  return false;
};

/**
 * Prefijos de país (ISO 3166-1 alfa-2) admitidos en NIF extranjero / VAT
 * intracomunitario. Cubre los 27 estados de la UE + XI (Irlanda del Norte) y
 * algunos terceros países habituales (GB, CH, NO, US, MX...).
 */
const FOREIGN_VAT_PREFIXES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","EL","GR","HU","IE",
  "IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","SE","XI",
  "GB","CH","NO","US","MX","AR","BR","CA","JP","CN",
]);

/**
 * Detecta si un NIF tiene prefijo de país extranjero. Devuelve
 * { country, number } o null si no es extranjero reconocido.
 * "ES" no se considera extranjero (se trata como NIF español sin prefijo).
 */
export const parseForeignTaxId = (
  raw: string | null | undefined,
): { country: string; number: string } | null => {
  const id = normalize(raw || "");
  if (!id || id.length < 4) return null;
  const prefix = id.slice(0, 2);
  if (prefix === "ES") return null;
  if (!FOREIGN_VAT_PREFIXES.has(prefix)) return null;
  const rest = id.slice(2);
  if (!/^[0-9A-Z]{2,13}$/.test(rest)) return null;
  return { country: prefix, number: rest };
};

/**
 * Validación de NIF para exportar a SUENLACE: acepta NIF/NIE/CIF español
 * con dígito de control correcto y también NIF extranjeros (UE / terceros)
 * con prefijo de país (ej. "IE9852817J", "FR12345678901").
 */
export const isValidTaxIdForExport = (raw: string | null | undefined): boolean => {
  if (isValidSpanishTaxId(raw)) return true;
  return parseForeignTaxId(raw) !== null;
};
